import { forwardRef } from "react";
import {
  Platform,
  ScrollView,
  StyleSheet,
  View,
  type ScrollViewProps,
  type ViewProps,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useTheme } from "../../hooks/useTheme";

/** Keep this the first native child of a route sheet so UIKit can find its scroll view. */
export const NativeSheetScrollView = forwardRef<ScrollView, ScrollViewProps>(
  function NativeSheetScrollView(
    { style, contentContainerStyle, ...props },
    ref,
  ) {
    const insets = useSafeAreaInsets();
    return (
      <ScrollView
        ref={ref}
        contentInsetAdjustmentBehavior="automatic"
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        nestedScrollEnabled
        {...props}
        style={[styles.scroll, style]}
        contentContainerStyle={[
          {
            paddingTop: 16,
            paddingBottom: 16 + (Platform.OS === "android" ? insets.bottom : 0),
          },
          contentContainerStyle,
        ]}
      />
    );
  },
);

/** A sibling of the scroller: its measured height is excluded from the scroll viewport. */
export function NativeSheetFooter({ style, ...props }: ViewProps) {
  const insets = useSafeAreaInsets();
  const { theme } = useTheme();
  return (
    <View
      {...props}
      style={[
        styles.footer,
        { backgroundColor: theme.background, borderTopColor: theme.border },
        style,
        { paddingBottom: Math.max(16, insets.bottom) },
      ]}
    />
  );
}

const styles = StyleSheet.create({
  scroll: { flex: 1, minHeight: 0 },
  footer: {
    flexShrink: 0,
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: 16,
    paddingTop: 12,
  },
});
